/**
 * The positives, grouped. The mirror of `SalesScoreBreakdown`, and deliberately not
 * the same table: there is no weight, no severity and no risk-points column, because
 * an opportunity cannot move the health score. Showing those columns empty would
 * invite someone to fill them in.
 *
 * Labels are rendered SERVER-SIDE, same rule as health reasons (§4.4) — the numbers
 * in "Ancillary revenue up 140%" come from the computation that produced the signal,
 * so the frontend never assembles one.
 *
 * The measured-over line matters more here than it looks. A positive and a negative
 * about the same measure are not in conflict — one is last week against the month, the
 * other the month against the quarter — but without the span stated, two true
 * statements look like a bug.
 */
import type { SalesOpportunity } from '../../api/types';

interface SalesOpportunityListProps {
  opportunities: SalesOpportunity[];
  /** The vocabulary from `/filter-options`, which carries the window text. Optional:
   *  before it loads the labels still stand on their own. */
  catalog?: SalesOpportunity[] | null;
}

export function SalesOpportunityList({ opportunities, catalog }: SalesOpportunityListProps) {
  if (opportunities.length === 0) return null;

  const windowsFor = new Map((catalog ?? []).map((c) => [c.code, c.windows]));

  // Preserve the order the server sent within a group; momentum before whitespace,
  // because "they are growing" changes the call and "they lack PSI" only adds to it.
  const groups: string[] = [];
  for (const o of opportunities) if (!groups.includes(o.group)) groups.push(o.group);

  return (
    <div className="sv-opps">
      {groups.map((group) => (
        <div className="sv-opp-group" key={group}>
          <div className="sv-opp-group-name">{group}</div>
          <ul className="sv-opp-list">
            {opportunities
              .filter((o) => o.group === group)
              .map((o) => {
                const windows = o.windows ?? windowsFor.get(o.code);
                return (
                  <li className="sv-opp" key={o.code}>
                    <span className="sv-opp-dot" aria-hidden="true" />
                    <span className="sv-opp-text">
                      <span className="sv-opp-label">{o.label}</span>
                      {windows ? <span className="sv-opp-windows">{windows}</span> : null}
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      ))}
      <p className="sv-opp-note">
        These never affect the health score. Plan My Week weighs everything but whitespace when
        it builds your week; whitespace is an agenda, not a ranking.
      </p>
    </div>
  );
}
